import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  Box,
  HStack,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import {TablerClock, TablerUsers, TablerSearch} from '../../icons';
import {appointmentSortKey} from '../../utils/appointmentStatus';

// ── useLazyVisibleCount ───────────────────────────────────────────
//
// Renders the first `initialCount` items immediately, then reveals the
// rest in batches only as the user scrolls toward the bottom of the
// list. This keeps the DOM small: a clinic with a 500+ patient day no
// longer mounts every row up front (which froze the main thread and
// bogged the whole tab down on login). Instead a sentinel placed after
// the last rendered row drives an IntersectionObserver — when it nears
// the viewport (or the provided scroll container), we append another
// `step` rows.
//
// Returns `[visibleCount, sentinelRef]`. Mount `sentinelRef` on a small
// element right after the rendered rows; when `visibleCount >= total`
// the sentinel can be omitted.
function useLazyVisibleCount(
  total: any,
  {
    initialCount = 25,
    step = 25,
    rootRef,
    // Start loading the next batch a bit before the sentinel is actually
    // on screen so scrolling feels seamless rather than stop-and-go.
    rootMargin = '0px 0px 600px 0px',
  }: Record<string, any> = {},
) {
  const [count, setCount] = useState(() => Math.min(initialCount, total));
  const sentinelRef = useRef<any>(null);

  // Grow up to (at least) the initial batch when data arrives or `total`
  // changes, and clamp down if the list shrank. Never lowers an already
  // larger count, so a user who has scrolled keeps their revealed rows
  // when the roster live-updates.
  useEffect(() => {
    setCount(current => {
      const floor = Math.min(initialCount, total);
      return Math.min(Math.max(current, floor), total);
    });
  }, [total, initialCount]);

  useEffect(() => {
    if (count >= total) return undefined;
    const node = sentinelRef.current;
    if (!node) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setCount(total);
      return undefined;
    }
    const root = rootRef?.current ?? null;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setCount(current => Math.min(current + step, total));
        }
      },
      {root, rootMargin},
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [count, total, step, rootRef, rootMargin]);

  return [Math.min(count, total), sentinelRef] as [number, any];
}

// ── PatientPickerSections ─────────────────────────────────────────
//
// Default-view layout for EHR-enabled patient pickers. Renders three
// stacked sections — "Today's appointments", "Recent patients", and
// (optional) a roster-size hint — using a render-prop for rows so each
// picker keeps its own row visual treatment.
//
// Why a shared component (instead of inline JSX in each picker):
//   - The "browse first 200 alphabetical" pattern silently truncated
//     the patient list and caused real "I can't find my patient"
//     bugs. Centralising the new sectioned default view keeps every
//     remaining patient-selection surface honest about what it shows by default.
//   - The dedup logic (Recent rows that also have a Today appointment
//     are pinned to Today only) lives here so each picker can't drift.
//
// The sections only appear when their lists are non-empty. When ALL
// sections are empty (no appointments today + no recents yet) we fall
// back to the `emptyHint` slot so the default view is never just a void.
//
// Props:
//   unifiedPatients              The currently-loaded unified list
//                                (from buildUnifiedPatientList) — used
//                                only as the source for "Today's
//                                appointments" rows.
//   recentPatients               Items from `useRecentPatients`, already
//                                shaped like unified-list items.
//   appointmentStatusByPatientId Map<ehrPatientId, status> from
//                                `useAppointmentStatuses`.
//   getEhrPatientIdForItem       (item) => string | null  — picker's own
//                                resolver (mirrors what the picker uses
//                                for badge logic so we stay consistent).
//   renderRow                    ({ item, sectionKey, indexInSection,
//                                  apptStatus }) => ReactNode — picker
//                                provides its own row UI.
//   emptyHint                    Optional ReactNode rendered when both
//                                sections are empty. Defaults to a
//                                muted "type to search" hint.
//   roster                       Optional { count, type } — when
//                                provided, renders a hint footer:
//                                "Search by name, DOB, or MRN — your
//                                EHR roster has 12,431 patients."
//   showRecent                   Defaults true. Set false when the
//                                caller has its own recents UI.
//   pinnedKinds                  Which appointment kinds are pinned to
//                                "Today's appointments". Defaults to
//                                ['now', 'soon', 'justEnded',
//                                'laterToday'] — i.e. anything not
//                                "past" or "unknown" surfaces here.
const DEFAULT_PINNED_KINDS = ['now', 'soon', 'justEnded', 'laterToday'];

function SectionHeader({
  icon,
  label,
  hint,
  accent = 'opalite.500',
}: Record<string, any>) {
  const subtle = useColorModeValue('gray.500', 'gray.400');
  return (
    <HStack spacing={2} px={1} pb={2}>
      <Box color={accent} display="inline-flex" alignItems="center">
        {icon}
      </Box>
      <Text
        fontSize="xs"
        fontWeight="normal"
        color={accent}
        textTransform="uppercase"
        letterSpacing="0.06em"
      >
        {label}
      </Text>
      {hint ? (
        <Text fontSize="xs" color={subtle}>
          · {hint}
        </Text>
      ) : null}
    </HStack>
  );
}

export default function PatientPickerSections({
  unifiedPatients = [],
  recentPatients = [],
  appointmentStatusByPatientId,
  getEhrPatientIdForItem,
  renderRow,
  emptyHint,
  roster,
  showRecent = true,
  pinnedKinds = DEFAULT_PINNED_KINDS,
  containerProps = {},
  // Scroll container the list lives in. Used as the IntersectionObserver
  // root so we can preload the next batch slightly before the user hits
  // the bottom. Optional — falls back to the viewport when omitted.
  scrollRootRef,
}: Record<string, any>) {
  const subtle = useColorModeValue('gray.500', 'gray.400');
  const hintBg = useColorModeValue('gray.50', 'gray.700');

  const todayItems = useMemo(() => {
    if (!appointmentStatusByPatientId || !appointmentStatusByPatientId.size)
      return [];
    const allowed = new Set(pinnedKinds);
    const seen = new Set();
    const out = [];
    for (const item of unifiedPatients) {
      const ehrId = getEhrPatientIdForItem?.(item);
      if (!ehrId) continue;
      const status = appointmentStatusByPatientId.get(ehrId);
      if (!status || !allowed.has(status.kind)) continue;
      const key = item.id || `today_${ehrId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({item, status});
    }
    out.sort(
      (a, b) => appointmentSortKey(a.status) - appointmentSortKey(b.status),
    );
    return out;
  }, [
    unifiedPatients,
    appointmentStatusByPatientId,
    getEhrPatientIdForItem,
    pinnedKinds,
  ]);

  // Patients already pinned to "Today" should not also appear under
  // "Recent" — Today is the more useful framing for them, and showing
  // them twice is just visual noise. We dedup by both ids and (as a
  // fallback) by the `recent_<eid>_<lid>` shape.
  const todayKeys = useMemo(() => {
    const set = new Set();
    for (const {item} of todayItems) {
      const ehrId = getEhrPatientIdForItem?.(item);
      if (ehrId) set.add(`ehr:${ehrId}`);
      const lid = item?.localPatient?.id;
      if (lid) set.add(`local:${lid}`);
    }
    return set;
  }, [todayItems, getEhrPatientIdForItem]);

  const recentItemsFiltered = useMemo(() => {
    if (!showRecent) return [];
    return recentPatients.filter((item: any) => {
      const ehrId = getEhrPatientIdForItem?.(item);
      if (ehrId && todayKeys.has(`ehr:${ehrId}`)) return false;
      const lid = item?.localPatient?.id;
      if (lid && todayKeys.has(`local:${lid}`)) return false;
      return true;
    });
  }, [recentPatients, todayKeys, getEhrPatientIdForItem, showRecent]);

  // Lazily reveal today's appointments as the user scrolls. A big EHR
  // clinic can have hundreds of appointments in a day; mounting them all
  // up front is what bogged the tab down on login. Recents are already
  // capped upstream (~10) so they don't need this.
  const [visibleTodayCount, todaySentinelRef] = useLazyVisibleCount(
    todayItems.length,
    {rootRef: scrollRootRef},
  );
  const visibleTodayItems = useMemo(
    () => todayItems.slice(0, visibleTodayCount),
    [todayItems, visibleTodayCount],
  );
  const hasMoreToday = visibleTodayCount < todayItems.length;

  const hasToday = todayItems.length > 0;
  const hasRecent = recentItemsFiltered.length > 0;
  const isEmpty = !hasToday && !hasRecent;

  const defaultHint = (
    <HStack
      spacing={2}
      px={3}
      py={3}
      bg={hintBg}
      borderRadius="lg"
      align="center"
    >
      <Box color={subtle} display="inline-flex">
        <TablerSearch size={14} />
      </Box>
      <Text fontSize="sm" color={subtle}>
        Type a name, DOB, or MRN to search.
      </Text>
    </HStack>
  );

  return (
    <VStack spacing={4} align="stretch" {...containerProps}>
      {hasToday && (
        <Box>
          <SectionHeader
            icon={<TablerClock size={12} />}
            label="Today's appointments"
            hint={`${todayItems.length} ${todayItems.length === 1 ? 'patient' : 'patients'}`}
            accent="gray.600"
          />
          <VStack spacing={1} align="stretch">
            {visibleTodayItems.map(({item, status}, indexInSection) => (
              <React.Fragment key={item.id || `today_${indexInSection}`}>
                {renderRow({
                  item,
                  sectionKey: 'today',
                  indexInSection,
                  apptStatus: status,
                })}
              </React.Fragment>
            ))}
          </VStack>
          {hasMoreToday && (
            <HStack ref={todaySentinelRef} justify="center" py={3} spacing={2}>
              <Spinner
                size="xs"
                color="opalite.500"
                thickness="2px"
                speed="0.7s"
              />
              <Text fontSize="xs" color={subtle}>
                {todayItems.length - visibleTodayCount} more
              </Text>
            </HStack>
          )}
        </Box>
      )}

      {hasRecent && (
        <Box>
          <SectionHeader
            icon={<TablerUsers size={12} />}
            label="Recent patients"
            hint="From your recent sessions"
            accent="opalite.500"
          />
          <VStack spacing={1} align="stretch">
            {recentItemsFiltered.map((item: any, indexInSection: any) => {
              const ehrId = getEhrPatientIdForItem?.(item);
              const status = ehrId
                ? appointmentStatusByPatientId?.get(ehrId)
                : null;
              return (
                <React.Fragment key={item.id || `recent_${indexInSection}`}>
                  {renderRow({
                    item,
                    sectionKey: 'recent',
                    indexInSection,
                    apptStatus: status,
                  })}
                </React.Fragment>
              );
            })}
          </VStack>
        </Box>
      )}

      {isEmpty && (emptyHint ?? defaultHint)}

      {roster && typeof roster.count === 'number' && roster.count > 0 && (
        <Text fontSize="xs" color={subtle} textAlign="center" pt={1}>
          Search by name, DOB, or MRN — your EHR roster has{' '}
          <Text
            as="span"
            fontWeight="normal"
            sx={{fontVariantNumeric: 'tabular-nums'}}
          >
            {roster.count.toLocaleString()}
          </Text>{' '}
          {roster.count === 1 ? 'patient' : 'patients'}.
        </Text>
      )}
    </VStack>
  );
}
